import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { prefsStore, useApi, useSession, useSessionContext } from '@adiox/ui';

/** Whether the server may email this user about margin calls, stop outs, challenge results, payouts, alerts and messages. */
export function EmailNotificationsSection() {
  const api = useApi();
  const readOnly = useSession().user?.readOnly === true;
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const settings = useQuery({
    queryKey: ['notification-settings'],
    enabled: !readOnly,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/me/notifications');
      if (!data) throw new Error('settings failed');
      return data;
    },
  }).data;
  if (readOnly || !settings) return null;

  return (
    <div className="flex flex-col gap-1 text-sm">
      {(
        [
          [
            'emailAlerts',
            'Email me about margin calls, stop outs, challenge results, payouts, price alerts and new messages',
          ],
          [
            'emailStatements',
            'Email me a statement of the day, after each rollover, on days my account had activity',
          ],
        ] as const
      ).map(([key, text]) => (
        <label key={key} className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings[key]}
            disabled={saving}
            onChange={async (e) => {
              setSaving(true);
              setProblem(null);
              const { data, response } = await api.PUT('/api/v1/me/notifications', {
                body: { [key]: e.target.checked },
              });
              setSaving(false);
              if (response.ok && data) queryClient.setQueryData(['notification-settings'], data);
              else setProblem('The setting could not be saved.');
            }}
          />
          {text}
        </label>
      ))}
      {!settings.emailAvailable && (
        <p className="text-xs text-muted">
          Email is not set up on this server yet, so nothing is sent for now.
        </p>
      )}
      {problem && (
        <p role="alert" className="text-xs text-down">
          {problem}
        </p>
      )}
    </div>
  );
}

/** MT5's "Open an account": another demo account for this user, switched to at once. */
export function DemoAccountSection() {
  const api = useApi();
  const { tokens } = useSessionContext();
  const user = useSession().user;
  const [offered, setOffered] = useState<{ deposit: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void tokens.signInOptions?.().then((o) => {
      if (!cancelled && o?.demoRegistration) setOffered({ deposit: o.demoDeposit });
    });
    return () => {
      cancelled = true;
    };
  }, [tokens]);

  if (!offered || !user || user.readOnly || user.role !== 'Client') return null;

  const open = async () => {
    setBusy(true);
    setMessage(null);
    const { data, error, response } = await api.POST('/api/v1/me/demo-accounts');
    if (response.ok && data) {
      await tokens.refresh(tokens.accessToken); // the session's account list now includes it
      prefsStore.set('activeLogin', data.login);
      setMessage({
        tone: 'ok',
        text: `Demo account ${data.login} is open, and the terminal switched to it.`,
      });
    } else {
      const detail = (error as { detail?: string } | undefined)?.detail;
      setMessage({
        tone: 'error',
        text: detail
          ? detail.charAt(0).toUpperCase() + detail.slice(1) + '.'
          : 'The account could not be opened.',
      });
    }
    setBusy(false);
  };

  return (
    <div className="flex flex-col gap-1 text-sm">
      <button
        type="button"
        disabled={busy}
        onClick={() => void open()}
        className="self-start rounded border border-line px-3 py-1 hover:border-accent disabled:opacity-40"
      >
        {busy ? 'Opening…' : 'Open a demo account'}
      </button>
      <p className="text-xs text-muted">
        Practise with {offered.deposit.toLocaleString('en-US')} of virtual money. It appears in the account
        list next to your others.
      </p>
      {message && (
        <p role="status" className={`text-xs ${message.tone === 'ok' ? 'text-up' : 'text-down'}`}>
          {message.text}
        </p>
      )}
    </div>
  );
}
